import { ShipmentOrderResponseEntity } from 'src/entities/ShipmentOrderResponse.entity';

/**
 * 複数伝票のチェック制約（指定車両タイプ等）を和集合にし、マスタの選択肢順で並べる。
 * flatMap + Set だけだと伝票の並び次第でチップの順が「4t, 10t, 2t」のように崩れる。
 */
export const mergeConstraintSelectionsByMasterOrder = <T extends string>(
  orders: ShipmentOrderResponseEntity[],
  pick: (o: ShipmentOrderResponseEntity) => T[] | undefined,
  masterOrder: readonly T[],
): T[] => {
  const set = new Set(
    orders.flatMap((o) => pick(o) ?? []).filter((v): v is T => Boolean(v)),
  );
  const inMaster = masterOrder.filter((opt) => set.has(opt));
  const unknown = [...set]
    .filter((v) => !masterOrder.some((opt) => opt === v))
    .sort((a, b) => a.localeCompare(b, 'ja'));
  return [...inMaster, ...unknown];
};

/** 表示用: 重複を除いて連結 */
export const mergeUniqueStrings = (
  values: (string | null | undefined)[],
  separator = ', ',
): string => {
  const u = [...new Set(values.filter((v): v is string => Boolean(v)))];
  return u.length ? u.join(separator) : '—';
};

/** 貨物種別・荷扱いなどのマージ表示 */
export const mergeUniqueFieldValues = <T extends string | undefined>(
  orders: ShipmentOrderResponseEntity[],
  pick: (o: ShipmentOrderResponseEntity) => T,
): string => mergeUniqueStrings(orders.map(pick));

export const mergeItemCanBeMixedLabel = (orders: ShipmentOrderResponseEntity[]): string => {
  const set = new Set(orders.map((o) => o.itemCanBeMixed));
  if (set.size <= 1) {
    return orders[0]?.itemCanBeMixed ? '積み合わせ可能' : '積み合わせ不可';
  }
  return '伝票間で異なります';
};

/** 区間種別（LEG stats）のマージ */
export const mergeLegStatsDisplay = (orders: ShipmentOrderResponseEntity[]): string => {
  const stats = orders
    .map((o) => o.shipmentOrderLegs?.[0]?.stats)
    .filter((s): s is string => typeof s === 'string' && s.length > 0);
  return mergeUniqueStrings(stats);
};

/** 会社マスタの任意項目名 + 各伝票に存在するキーの和集合 */
export const collectCustomInputFieldKeys = (
  companyFieldNames: string[],
  orders: ShipmentOrderResponseEntity[],
): string[] => {
  const keys = new Set<string>(companyFieldNames);
  orders.forEach((o) => {
    o.customInputFields?.forEach((f) => keys.add(f.key));
  });
  return Array.from(keys);
};

/** ドラフト初期化用: キーごとに先に見つかった値 */
export const buildCustomInputEntriesForDraft = (
  keys: string[],
  orders: ShipmentOrderResponseEntity[],
): { key: string; value: string }[] =>
  keys.map((key) => {
    const found = orders.flatMap((o) => o.customInputFields || []).find((f) => f.key === key);
    return { key, value: found?.value ?? '' };
  });
